import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject, merge, of } from "rxjs";
import {
    OccurenceType,
    OccurenceTypeClient,
    UpsertOccurenceTypeRequest,
} from "../WebApiClient";
import { Guid } from "../models/Guid";
import {
    map,
    publishReplay,
    refCount,
    switchMap,
} from "rxjs/operators";

@Injectable()
export class OccurenceTypeService {

    private readonly occurenceTypeTrigger = new BehaviorSubject<boolean>(true);

    readonly occurenceTypes: Observable<{
        occurenceTypes: OccurenceType[];
        loading: boolean;
    }>;

    constructor(private readonly occurenceTypeClient: OccurenceTypeClient) {
        let oldOccurenceTypes: OccurenceType[] = [];
        this.occurenceTypes = this.occurenceTypeTrigger.pipe(
            switchMap((shouldResolve) =>
                shouldResolve
                    ? merge(
                          of<undefined>(undefined),
                          occurenceTypeClient.getOccurenceTypes()
                      )
                    : of<undefined>(undefined)
            ),
            map((response) => {
                if (response) {
                    oldOccurenceTypes = response.occurenceTypes;
                }
                return {
                    occurenceTypes: oldOccurenceTypes,
                    loading: !response,
                };
            }),
            publishReplay(2),
            refCount()
        );
    }

    async addOccurenceType(name: string): Promise<void> {
        try {
            this.occurenceTypeTrigger.next(false);
            await this.occurenceTypeClient
                .upsertOccurenceType(
                    new UpsertOccurenceTypeRequest({
                        id: Guid.newGuid(),
                        name: name,
                    })
                )
                .toPromise();
        } finally {
            this.occurenceTypeTrigger.next(true);
        }
    }

    async updateOccurenceType(id: string, name: string) {
        try {
            this.occurenceTypeTrigger.next(false);
            await this.occurenceTypeClient
                .upsertOccurenceType(
                    new UpsertOccurenceTypeRequest({
                        id: id,
                        name: name,
                    })
                )
                .toPromise();
        } finally {
            this.occurenceTypeTrigger.next(true);
        }
    }

    async deleteOccurenceType(id: string) {
        try {
            this.occurenceTypeTrigger.next(false);
            await this.occurenceTypeClient.deleteOccurenceType(id).toPromise();
        } finally {
            this.occurenceTypeTrigger.next(true);
        }
    }
}
